import API from "serviceshift-ui/api-client";

import { trackEvent } from "@/lib/pendo";

// Admin pay-by-link actions (TB-1884). The URLs come from the invoice payload
// (`invoice.links.payby` / `revoke_pay_link`) so the front end never hardcodes
// paths; send is POST, revoke is DELETE on the same endpoint.

interface SendPayLinkParams {
  url: string;
  phoneNumber?: string;
}

interface SendPayLinkEmailParams {
  url: string;
  email?: string;
}

interface RevokePayLinkParams {
  url: string;
}

interface SendResult {
  sent: boolean;
  invoice_id: number;
}

interface RevokeResult {
  revoked: boolean;
  invoice_id: number;
}

// Pendo send events (TB-2031). Two deliberate naming choices:
//
// 1. The channel lives in the event NAME, not only in a property. Pendo's query
//    surface cannot filter or break a track event out by event property, so a
//    single `payment_link_sent` carrying { channel } would not answer
//    "email vs SMS" — the one question this instrumentation exists for.
// 2. No `spb_` prefix. In this app `spb` means progress_billing, and every
//    existing `spb_*` event fires from a Progress Billing surface, so prefixing
//    these would file pay-link sends under Progress Billing. Non-SPB admin
//    events are unprefixed (`partial_refund_completed`, `media_caption_edited`).
//
// `payment_link_` also lines up with the mobile tech app's `payment_sms_sent` /
// `payment_qr_generated` / `payment_email_sent`.
const PAY_LINK_SENT_EVENTS = {
  sms: "payment_link_sent_sms",
  email: "payment_link_sent_email"
} as const;

// Deduped per invoice + channel, matching mobile and TB-2031's "deduped per
// invoice" AC. The guard sits at module scope because SendPayLinkModal is
// remounted on every open, so component state would reset between a send and a
// later resend of the same invoice.
const trackedSends = new Set<string>();

// No customer_id: Pendo already attributes the event to the admin visitor and the
// tenant account, and threading it here would change the send actions' payload
// contract for telemetry alone.
function trackPayLinkSent(
  channel: keyof typeof PAY_LINK_SENT_EVENTS,
  result: SendResult
): void {
  // Only a truthful `sent` counts. Failures render 422 (PaybyResponder
  // #render_payby_error) and reject before reaching here, but gating on the
  // contract flag keeps a future 200-with-error-body from inflating send counts.
  if (!result?.sent) return;

  const key = `${result.invoice_id}:${channel}`;
  if (trackedSends.has(key)) return;
  trackedSends.add(key);

  // Telemetry must never fail the send. Without this, a throw from the Pendo
  // agent rejects the action, the modal's catch treats it as a failed send, and
  // the admin gets an error toast for a link the customer already received.
  try {
    trackEvent(PAY_LINK_SENT_EVENTS[channel], {
      channel,
      invoice_id: result.invoice_id
    });
  } catch {
    // analytics is best-effort; the send already succeeded
  }
}

export default {
  async sendPayLink(
    _: unknown,
    { url, phoneNumber }: SendPayLinkParams
  ): Promise<SendResult> {
    const response = await API.makeRequest<SendResult>(url, {
      method: "POST",
      data: phoneNumber ? { phone_number: phoneNumber } : {}
    });
    trackPayLinkSent("sms", response.data);
    return response.data;
  },

  // Pay-by-email send (TB-2027). POSTs to invoice.links.payby_email; `email`
  // overrides the recipient and the server saves a new address to the
  // customer's additional emails (see PaybyResponder#resolve_email).
  async sendPayLinkEmail(
    _: unknown,
    { url, email }: SendPayLinkEmailParams
  ): Promise<SendResult> {
    const response = await API.makeRequest<SendResult>(url, {
      method: "POST",
      data: email ? { email } : {}
    });
    trackPayLinkSent("email", response.data);
    return response.data;
  },

  async revokePayLink(
    _: unknown,
    { url }: RevokePayLinkParams
  ): Promise<RevokeResult> {
    const response = await API.makeRequest<RevokeResult>(url, {
      method: "DELETE"
    });
    return response.data;
  }
};
